import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Save, X, Upload, BookOpen, GraduationCap, Image as ImageIcon, Video } from "lucide-react";

/**
 * CourseBuilderTab — Mike's "do it myself" admin page for adding courses.
 *
 * Mike (admin) can:
 *  - Add a new course (title, description, thumbnail, track, "free for everyone" toggle)
 *  - Add modules / lessons under each course (title, description, video URL, image)
 *  - Edit or delete his custom courses
 *  - Upload images directly from his computer (no external link needed)
 *
 * The 11 built-in seed courses are READ-ONLY — they show in the list but Mike's
 * own custom courses are the only ones he can edit/delete.
 */

const TRACKS = [
  { value: "christian", label: "Christian Faith" },
  { value: "business", label: "Business / How-To" },
  { value: "book", label: "From Mike's Book" },
];

function FileUploader({ onUploaded, label = "Upload from my computer" }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isImage = (f.type || "").startsWith("image/");
    const isVideo = (f.type || "").startsWith("video/");
    if (isImage && f.size > 15 * 1024 * 1024) { toast.error("Image too large (max 15MB). Compress it at tinypng.com and try again."); return; }
    if (isVideo && f.size > 500 * 1024 * 1024) { toast.error(`Video too large (${Math.round(f.size/1024/1024)}MB · max 500MB). Compress it at clipchamp.com or handbrake.fr first.`); return; }
    if (!isImage && !isVideo && f.size > 200 * 1024 * 1024) { toast.error("File too large (max 200MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      // Route images to /uploads/image, other files (videos, PDFs, etc.) to /uploads/file
      const endpoint = isImage ? "/uploads/image" : "/uploads/file";
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — check the file type and try again");
    }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider-cap cursor-pointer text-foreground/80 hover:text-red-wine border border-border rounded-md px-3 py-1.5 hover:bg-white/50">
      <Upload className="w-3.5 h-3.5"/> {busy ? "Uploading…" : label}
      <input type="file" accept="image/*,video/*" onChange={handle} className="hidden" disabled={busy}/>
    </label>
  );
}

function ModuleEditor({ courseId, lesson, onChange, onDelete }) {
  const [edit, setEdit] = useState(!lesson.id || lesson.id.startsWith("draft-"));
  const [form, setForm] = useState({
    title: lesson.title || "",
    description: lesson.description || "",
    video_url: lesson.video_url || "",
    image: lesson.image || "",
  });

  const save = async () => {
    if (!form.title.trim()) { toast.error("Module title is required"); return; }
    try {
      if (lesson.id && !lesson.id.startsWith("draft-")) {
        await api.put(`/admin/courses/${courseId}/lessons/${lesson.id}`, form);
        toast.success("Module saved");
      } else {
        const r = await api.post(`/admin/courses/${courseId}/lessons`, form);
        onChange(r.data.lesson);
      }
      setEdit(false);
      onChange({ ...lesson, ...form });
    } catch { toast.error("Could not save module"); }
  };

  const remove = async () => {
    if (!confirm(`Delete module "${form.title || lesson.title}"?`)) return;
    if (lesson.id && !lesson.id.startsWith("draft-")) {
      try { await api.delete(`/admin/courses/${courseId}/lessons/${lesson.id}`); } catch {}
    }
    onDelete();
  };

  return (
    <div className="border border-border rounded-md p-4 bg-white" data-testid={`module-editor-${lesson.id}`}>
      {edit ? (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Module Title *</label>
              <input
                className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
                placeholder="e.g. Reality Check · Nothing Here Lasts"
                value={form.title} onChange={e=>setForm({...form, title:e.target.value})}
                data-testid="module-title-input"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Video URL</label>
              <input
                className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
                placeholder="Paste a YouTube or Vimeo link · OR click Upload Video"
                value={form.video_url} onChange={e=>setForm({...form, video_url:e.target.value})}
                data-testid="module-video-input"/>
              <p className="text-[11px] text-foreground/70 mt-1 leading-relaxed">
                <strong className="text-red-wine">★ Recommended:</strong> Upload your video to YouTube (set to <strong>Unlisted</strong>) and paste the link here — <strong>no size limit, auto captions, faster for students</strong>. Works with <code>youtu.be/…</code>, <code>youtube.com/watch?v=…</code>, and <code>vimeo.com/…</code> links.
              </p>
              <div className="mt-1.5">
                <FileUploader onUploaded={(url) => setForm(f => ({...f, video_url: url}))} label="Or Upload MP4 (max 500MB)"/>
              </div>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Description / What students will learn</label>
            <textarea rows={3}
              className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
              placeholder="A few sentences about this module."
              value={form.description} onChange={e=>setForm({...form, description:e.target.value})}
              data-testid="module-description-input"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Module Image / Thumbnail</label>
            <div className="flex items-center gap-3 flex-wrap">
              <input
                className="flex-1 min-w-0 px-3 py-2 rounded-md border border-border bg-white text-sm"
                placeholder="Image URL"
                value={form.image} onChange={e=>setForm({...form, image:e.target.value})}
                data-testid="module-image-input"/>
              <FileUploader onUploaded={(url) => setForm(f => ({...f, image: url}))} label="Upload Image"/>
              {form.image && <img src={form.image} alt="" className="h-12 w-16 object-cover rounded border border-border"/>}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setEdit(false)} className="btn-outline-gold !py-1.5 !px-3 !text-xs">Cancel</button>
            <button onClick={save} className="btn-primary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5" data-testid="module-save-btn">
              <Save className="w-3.5 h-3.5"/> Save Module
            </button>
            <button onClick={remove} className="text-red-wine hover:text-[#1F8A2A] !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5"/> Delete
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          {form.image ? <img src={form.image} alt="" className="h-12 w-16 object-cover rounded border border-border shrink-0"/> :
            <div className="h-12 w-16 rounded border border-border bg-white flex items-center justify-center shrink-0"><ImageIcon className="w-4 h-4 text-foreground/40"/></div>}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-foreground truncate">{form.title}</div>
            <div className="text-[11px] text-foreground/60">
              {form.video_url ? <span className="inline-flex items-center gap-1 mr-3"><Video className="w-3 h-3"/> Video set</span> : <span className="text-foreground/40 mr-3">No video</span>}
              {form.description ? `${form.description.slice(0, 80)}${form.description.length > 80 ? "…" : ""}` : <em>No description</em>}
            </div>
          </div>
          <button onClick={() => setEdit(true)} className="text-foreground/70 hover:text-red-wine p-1.5" data-testid={`edit-module-${lesson.id}`}>
            <Edit3 className="w-4 h-4"/>
          </button>
        </div>
      )}
    </div>
  );
}

function CourseEditor({ course, onSaved, onDelete }) {
  const [open, setOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: course.title,
    description: course.description || "",
    image: course.image || "",
    track: course.track || "christian",
    free_for_all: !!course.free_for_all,
  });
  const [lessons, setLessons] = useState(course.lessons || []);

  const saveCourse = async () => {
    try {
      await api.put(`/admin/courses/${course.id}`, editForm);
      toast.success("Course saved");
      onSaved && onSaved();
    } catch { toast.error("Could not save"); }
  };

  const removeCourse = async () => {
    if (!confirm(`Delete course "${course.title}"? This deletes all modules and student enrollments for this course.`)) return;
    try {
      await api.delete(`/admin/courses/${course.id}`);
      toast.success("Course deleted");
      onDelete();
    } catch { toast.error("Could not delete"); }
  };

  const addLesson = () => {
    setLessons(ls => [...ls, { id: `draft-${Date.now()}`, title: "", description: "", video_url: "", image: "" }]);
  };

  const updateLesson = (idx, patch) => setLessons(ls => ls.map((l,i) => i===idx ? { ...l, ...patch } : l));
  const removeLessonAt = (idx) => setLessons(ls => ls.filter((_,i) => i !== idx));

  return (
    <div className="faith-card p-5" data-testid={`course-editor-${course.id}`}>
      <div className="flex items-center gap-4">
        {editForm.image ? <img src={editForm.image} alt="" className="h-16 w-24 object-cover rounded border border-border shrink-0"/> :
          <div className="h-16 w-24 rounded border border-border bg-white flex items-center justify-center shrink-0"><BookOpen className="w-5 h-5 text-foreground/40"/></div>}
        <div className="flex-1 min-w-0">
          <div className="font-serif-display text-lg text-red-wine uppercase truncate">{editForm.title}</div>
          <div className="text-[11px] text-foreground/70 mt-0.5">
            {editForm.free_for_all ? "Free for everyone" : "Members only"} · {lessons.length} module{lessons.length === 1 ? "" : "s"}
          </div>
        </div>
        <button onClick={() => setOpen(o => !o)} className="btn-outline-gold !py-1.5 !px-3 !text-xs" data-testid={`toggle-course-${course.id}`}>
          {open ? <><X className="w-3.5 h-3.5 inline mr-1"/>Close</> : <><Edit3 className="w-3.5 h-3.5 inline mr-1"/>Edit</>}
        </button>
      </div>
      {open && (
        <div className="mt-4 space-y-4 border-t border-border/50 pt-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Course Title *</label>
              <input className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
                value={editForm.title} onChange={e=>setEditForm({...editForm, title:e.target.value})}
                data-testid="course-title-input"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Track</label>
              <select className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
                value={editForm.track} onChange={e=>setEditForm({...editForm, track:e.target.value})}>
                {TRACKS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Description (the sales-page paragraph)</label>
            <textarea rows={3} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
              value={editForm.description} onChange={e=>setEditForm({...editForm, description:e.target.value})}/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Course Thumbnail (Cover Image)</label>
            <div className="flex items-center gap-3 flex-wrap">
              <input className="flex-1 min-w-0 px-3 py-2 rounded-md border border-border bg-white text-sm"
                placeholder="Image URL"
                value={editForm.image} onChange={e=>setEditForm({...editForm, image:e.target.value})}/>
              <FileUploader onUploaded={(url) => setEditForm(f => ({...f, image: url}))} label="Upload Thumbnail"/>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer">
            <input type="checkbox" className="accent-[#2E9F3A]"
              checked={editForm.free_for_all} onChange={e=>setEditForm({...editForm, free_for_all:e.target.checked})}/>
            Free for everyone (including non-members)
          </label>
          <div className="flex gap-2 justify-end">
            <button onClick={removeCourse} className="text-red-wine hover:text-[#1F8A2A] py-1.5 px-3 text-xs uppercase tracking-wider-cap font-bold inline-flex items-center gap-1.5" data-testid={`delete-course-${course.id}`}>
              <Trash2 className="w-3.5 h-3.5"/> Delete Course
            </button>
            <button onClick={saveCourse} className="btn-primary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5" data-testid={`save-course-${course.id}`}>
              <Save className="w-3.5 h-3.5"/> Save Course
            </button>
          </div>

          {/* MODULES */}
          <div className="border-t border-border/50 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold uppercase tracking-wider-cap text-red-wine">Modules / Lessons</h4>
              <button onClick={addLesson} className="btn-gold !py-1.5 !px-3 !text-[10px] inline-flex items-center gap-1.5" data-testid={`add-module-${course.id}`}>
                <Plus className="w-3.5 h-3.5"/> Add Module
              </button>
            </div>
            {lessons.length === 0 ? (
              <div className="text-center text-foreground/60 text-sm py-6 bg-white rounded">
                No modules yet — click "Add Module" to add the first one.
              </div>
            ) : (
              <div className="space-y-2">
                {lessons.map((l, i) => (
                  <ModuleEditor
                    key={l.id || i}
                    courseId={course.id}
                    lesson={l}
                    onChange={(patch) => updateLesson(i, patch)}
                    onDelete={() => removeLessonAt(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NewCourseForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", image: "", track: "christian", free_for_all: false, lesson_titles: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Course title is required"); return; }
    setBusy(true);
    try {
      const lessons = form.lesson_titles.split("\n").map(s => s.trim()).filter(Boolean).map(t => ({ title: t }));
      await api.post("/admin/courses", {
        title: form.title, description: form.description, image: form.image,
        track: form.track, category: "discipleship",
        free_for_all: form.free_for_all,
        lessons,
      });
      toast.success("Course created — now add the videos and details");
      setForm({ title: "", description: "", image: "", track: "christian", free_for_all: false, lesson_titles: "" });
      setOpen(false);
      onCreated();
    } catch { toast.error("Could not create"); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="w-full faith-card p-6 border-2 border-dashed border-[#F39A1F]/60 hover:border-[#F39A1F] hover:bg-white/50 transition flex items-center justify-center gap-3 text-red-wine font-bold uppercase tracking-wider-cap"
        data-testid="open-new-course-btn">
        <Plus className="w-5 h-5"/> Add A New Course
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="faith-card p-6 space-y-4" data-testid="new-course-form">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-xl text-red-wine uppercase">New Course</h3>
        <button type="button" onClick={() => setOpen(false)} className="text-foreground/60 hover:text-red-wine"><X className="w-5 h-5"/></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Title *</label>
          <input required className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            placeholder="e.g. Everything Falls Apart Unless Christianity Is True"
            value={form.title} onChange={e=>setForm({...form, title:e.target.value})}
            data-testid="new-course-title"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Track</label>
          <select className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            value={form.track} onChange={e=>setForm({...form, track:e.target.value})}>
            {TRACKS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Description</label>
        <textarea rows={2} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
          placeholder="One sentence about what they'll learn."
          value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Course Thumbnail</label>
        <div className="flex items-center gap-3 flex-wrap">
          <input className="flex-1 min-w-0 px-3 py-2 rounded-md border border-border bg-white text-sm"
            placeholder="Image URL or click Upload"
            value={form.image} onChange={e=>setForm({...form, image:e.target.value})}
            data-testid="new-course-image"/>
          <FileUploader onUploaded={(url) => setForm(f => ({...f, image: url}))} label="Upload Thumbnail"/>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Module Titles (one per line — you can fill in videos and details after creating)</label>
        <textarea rows={5} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm font-mono"
          placeholder={"Module 1: Reality Check\nModule 2: The Fork in the Road\nModule 3: ..."}
          value={form.lesson_titles} onChange={e=>setForm({...form, lesson_titles:e.target.value})}
          data-testid="new-course-modules"/>
      </div>
      <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer">
        <input type="checkbox" className="accent-[#2E9F3A]"
          checked={form.free_for_all} onChange={e=>setForm({...form, free_for_all:e.target.checked})}/>
        Free for everyone (including non-members)
      </label>
      <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
        <button type="button" onClick={() => setOpen(false)} className="btn-outline-gold !py-2 !px-4 !text-xs">Cancel</button>
        <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="new-course-create-btn">
          <Save className="w-4 h-4"/> {busy ? "Creating…" : "Create Course"}
        </button>
      </div>
    </form>
  );
}

export default function CourseBuilderTab() {
  const [customCourses, setCustomCourses] = useState([]);
  const [seedCount, setSeedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/courses");
      setCustomCourses(r.data.custom_courses || []);
      setSeedCount(r.data.seed_count || 0);
      setErr(null);
    } catch (e) {
      setErr(e.response?.status === 403 ? "Admin only" : "Could not load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-10 text-foreground/60">Loading…</div>;
  if (err === "Admin only") return (
    <div className="faith-card p-8 text-center">
      <div className="text-2xl text-red-wine font-serif-display uppercase">Admin Only</div>
      <p className="text-foreground/70 mt-2">Only the site owner can build courses.</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="course-builder-tab">
      <div>
        <div className="text-xs tracking-wider-cap text-gold-deep">★ Course Builder · Admin · Mike's Tool</div>
        <h2 className="text-3xl uppercase text-red-wine">Build Your Own Courses</h2>
        <p className="text-foreground/85 mt-2 max-w-2xl text-base">
          Add a course, type the module titles, upload videos &amp; images. Members will see them in the Academy automatically.
          Built-in starter courses ({seedCount}) are read-only — your custom courses are listed below.
        </p>
      </div>

      <NewCourseForm onCreated={load}/>

      {customCourses.length === 0 ? (
        <div className="text-center text-foreground/70 py-10 faith-card bg-white">
          <GraduationCap className="w-10 h-10 text-[#F39A1F]/60 mx-auto mb-2"/>
          <div className="font-bold uppercase tracking-wider-cap text-sm">No custom courses yet</div>
          <p className="text-foreground/60 text-sm mt-1">Click "Add A New Course" above to publish your first one.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="custom-courses-list">
          {customCourses.map(c => (
            <CourseEditor key={c.id} course={c} onSaved={load} onDelete={load}/>
          ))}
        </div>
      )}
    </div>
  );
}
